/**
 * preflightMode — discrete-state classifier for the report-wizard
 * preflight panel.
 *
 * The preflight surface is the first thing a user sees when they
 * start a new report. Today its rendering is implicit: scattered
 * `if (sed == null)` / `if (site.sedStatus === '…')` /
 * `if (siteReports.length === 0)` checks across multiple
 * components produce different UX in different combinations, with
 * no single source of truth for "which screen are we on?".
 *
 * This helper makes the modes explicit. Callers compute one of
 * five states and switch their rendering on it.
 *
 *   'first-run'  — site has 0 reports AND no SED. The next click
 *                  will bootstrap. Show a single welcoming card,
 *                  NOT the multi-panel preflight (it has no signal
 *                  yet — every section would be "missing", and the
 *                  user is about to fix exactly that).
 *   'bootstrap'  — site is mid-bootstrap right now. Wait for it.
 *   'failed'     — bootstrap failed. Show retry banner.
 *   'top-up'     — site has SED + may have reports; the next run
 *                  will collect a few stale sections. Render the
 *                  freshness card alongside the full preflight.
 *   'normal'     — fully-populated SED, every section fresh. Full
 *                  preflight + doc checklist.
 *
 * Pure / synchronous so the result can be memoised by callers
 * without re-running on every render.
 */
import type { SiteEntity } from '../types/site';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import type { SiteRagReport } from '../types/siteRag';
import { computeSedSectionStatuses } from './sedStaleness';

export type PreflightMode =
  | 'first-run'
  | 'bootstrap'
  | 'failed'
  | 'top-up'
  | 'normal';

export interface PreflightModeArgs {
  site: SiteEntity;
  sed: SiteEvidenceDocument | null;
  reports: SiteRagReport[];
}

export function getPreflightMode({ site, sed, reports }: PreflightModeArgs): PreflightMode {
  // Failed bootstrap shows the retry banner regardless of report
  // count — admin needs to act before the wizard is useful.
  if (site.sedStatus === 'failed') return 'failed';
  // In-flight bootstrap shows the waiting screen regardless of
  // whether older reports exist on the site.
  if (site.sedStatus === 'bootstrapping') return 'bootstrap';

  const reportsForSite = reports.filter(r => r.siteId === site.id);
  const hasReports = reportsForSite.length > 0;

  // First-run: no completed prior reports AND the site's SED isn't
  // marked complete. A partial SED counts as "not usable" — the user
  // still wants the welcome card, not the freshness panel listing
  // half the sections as missing.
  //
  // Critically, this gate is keyed on `site.sedStatus`, NOT on
  // whether `sed` is currently loaded. The wizard loads the SED
  // asynchronously via the most-recent-report path, so a freshly-
  // imported site (e.g. KMZ bootstrap) can have sedStatus='complete'
  // while `sed` is still transitioning. Routing that case to the
  // legacy preflight (which falls back gracefully when sed is null)
  // is correct — it means a user who imports a SED then opens the
  // wizard sees the freshness card instead of the welcome card.
  if (!hasReports && site.sedStatus !== 'complete') {
    return 'first-run';
  }

  // Top-up vs normal: top-up when any required section is stale
  // or missing. The freshness helper already classifies each
  // section; we just check for the presence of a non-fresh one.
  return topUpRecommended(sed) ? 'top-up' : 'normal';
}

function topUpRecommended(sed: SiteEvidenceDocument | null): boolean {
  if (!sed) return false;
  const statuses = computeSedSectionStatuses(sed);
  return statuses.some(s => s.staleness !== 'fresh');
}
